import { read, utils } from 'xlsx';

import {
  getScoringImportedShows,
  getScoringReviewedShows,
} from 'src/apis/selfScoring';
import {
  ReviewDataProps,
  ReviewedDataProps,
  SuggestedSuccessShowProps,
} from 'src/types/selfScoring';

const MAX_BYTES = 1048576;
const FILE_TYPES = ['csv', 'sheet', 'xls', 'xlsx'];

const toCellString = (v: unknown): string =>
  v === undefined || v === null || v === '' ? '' : String(v);

export async function parseImportFileRows(
  file: File
): Promise<Record<string, string>[]> {
  return new Promise((resolve, reject) => {
    if (
      file.size > MAX_BYTES ||
      FILE_TYPES.every((type) => !file.type.includes(type))
    ) {
      reject(new Error('Please upload a CSV or XLS file less than 1 MB.'));
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const wb = read(event?.target?.result);
        const sheets = wb.SheetNames;
        if (!sheets.length) {
          reject(new Error('No sheets found in file.'));
          return;
        }
        const rawRows = utils.sheet_to_json(wb.Sheets[sheets[0]], {
          raw: false,
          defval: '',
        }) as Record<string, unknown>[];
        const rows: Record<string, string>[] = rawRows.map((row) =>
          Object.fromEntries(
            Object.entries(row).map(([k, v]) => [k, toCellString(v)])
          )
        );
        if (!rows.length) {
          reject(new Error('Please add data to your file.'));
          return;
        }
        resolve(rows);
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    reader.onerror = () =>
      reject(new Error('Unable to read the selected file.'));
    reader.readAsArrayBuffer(file);
  });
}

export function canonicalizeImportedRows(
  rows: Record<string, string>[],
  opts: {
    isNoRSSFeed: boolean;
    customHeaders: { id: number; name: string; type: string }[];
  }
): Record<string, string>[] {
  const firstKeys = Object.keys(rows[0]);
  const lowerToOriginal = (k: string) => k.trim().toLowerCase();
  const findCol = (...aliases: string[]): string | undefined => {
    for (const alias of aliases) {
      const a = alias.toLowerCase();
      const hit = firstKeys.find(
        (k) =>
          lowerToOriginal(k) === a ||
          lowerToOriginal(k).includes(alias.toLowerCase())
      );
      if (hit !== undefined) {
        return hit;
      }
    }
    return undefined;
  };

  const titleCol =
    findCol('show title', 'show name', 'show', 'podcast title') ?? firstKeys[0];
  const rssCol = findCol('rss feed', 'rss url', 'feed url') ?? findCol('rss');

  const customColByName = new Map<string, string>();
  for (const ch of opts.customHeaders) {
    const col =
      findCol(ch.name.toLowerCase(), ch.name) ??
      firstKeys.find((fk) => fk.trim() === ch.name);
    if (col !== undefined) {
      customColByName.set(ch.name, col);
    }
  }

  return rows.map((row) => {
    const mapped: Record<string, string> = {
      'Show Title': String(row[titleCol] ?? '').trim(),
    };
    if (rssCol && row[rssCol] !== undefined) {
      mapped['RSS Feed'] = String(row[rssCol]).trim();
    } else {
      mapped['RSS Feed'] = '';
    }

    for (const ch of opts.customHeaders) {
      const col = customColByName.get(ch.name);
      if (col !== undefined && row[col] !== undefined) {
        mapped[ch.name] = String(row[col]);
      } else {
        mapped[ch.name] = '';
      }
    }

    return mapped;
  });
}

function validateMappedRowCustomTypes(
  mappedData: Record<string, string>[],
  customHeaders: { id: number; name: string; type: string }[]
): string | null {
  for (const item of mappedData) {
    for (const ch of customHeaders) {
      const value = item[ch.name];
      if (value === undefined || value === null || value === '') {
        continue;
      }
      switch (ch.type) {
        case 'number':
          if (Number.isNaN(Number(value))) {
            return `Invalid number in "${ch.name}".`;
          }
          break;
        case 'date': {
          const d = new Date(value);
          if (Number.isNaN(d.getTime())) {
            return `Invalid date in "${ch.name}" (use MM/DD/YY).`;
          }
          break;
        }
        default:
          break;
      }
    }
  }
  return null;
}

export function buildScoringRequestBodies(
  mappedData: Record<string, string>[],
  opts: {
    isNoRSSFeed: boolean;
    customHeaders: { id: number; name: string; type: string }[];
  }
): Record<string, unknown>[] {
  const { isNoRSSFeed, customHeaders } = opts;
  return mappedData.map((item) => {
    const showName = toCellString(item['Show Title']);
    const data: Record<string, unknown> = {
      showName,
      rss: isNoRSSFeed ? '' : toCellString(item['RSS Feed']),
    };
    if (customHeaders.length) {
      data.customColumns = customHeaders.map((ch) => ({
        fieldId: ch.id,
        value: toCellString(item[ch.name]),
      }));
    }
    return data;
  });
}

export function buildAutoReviewedData(data: ReviewDataProps): {
  reviewed: ReviewedDataProps;
  warnings: string[];
} {
  const warnings: string[] = [];
  const suggested: ReviewedDataProps['suggested'] = [];

  for (const item of data.suggested) {
    const choices = item.suggestedShows || [];
    if (choices.length === 0) {
      warnings.push(`No automatic match suggestion for "${item.showName}".`);
      continue;
    }
    suggested.push({
      showName: item.showName,
      rss: item.rss,
      suggestedShows: choices,
      selectedShow: choices[0],
      customColumns: item.customColumns,
    });
  }

  const invalid = data.invalid.filter((i) => {
    const ok = Boolean(i.rss?.trim());
    if (!ok) {
      warnings.push(`"${i.showName}" has invalid RSS and was skipped.`);
    }
    return ok;
  });

  const unmatched = data.unmatched.filter((u) => {
    const ok = Boolean(u.rss?.trim());
    if (!ok) {
      warnings.push(`Unmatched "${u.showName}" has no RSS and was skipped.`);
    }
    return ok;
  });

  return {
    reviewed: { suggested, invalid, unmatched },
    warnings,
  };
}

export function validateReviewedData(r: ReviewedDataProps): string | null {
  if (r.invalid.some((i) => !i.rss.trim())) {
    return 'Some invalid rows are missing RSS after automatic review.';
  }
  if (r.unmatched.some((x) => !x.rss.trim())) {
    return 'Some unmatched rows are missing RSS after automatic review.';
  }
  if (r.suggested.some((s) => !s.selectedShow.trim())) {
    return 'Some suggested rows could not receive an automatic selection.';
  }
  return null;
}

function uniqShowNames(shows: SuggestedSuccessShowProps[]): string[] {
  return Array.from(
    new Set(shows.map((s) => s.showName.trim()).filter(Boolean))
  );
}

export type PreferenceCsvPipelineResult = {
  showNames: string[];
  warnings: string[];
};

export async function processPreferenceCsvImport(options: {
  mappedData: Record<string, string>[];
  isNoRSSFeed: boolean;
  customHeaders: { id: number; name: string; type: string }[];
}): Promise<PreferenceCsvPipelineResult> {
  const { mappedData, isNoRSSFeed, customHeaders } = options;

  if (!mappedData.length) {
    throw new Error('No rows found in file.');
  }

  const headers = Object.keys(mappedData[0]);
  const hasShowTitle = headers.includes('Show Title');
  const hasRssFeed = headers.includes('RSS Feed');
  if (!hasShowTitle || (!isNoRSSFeed && !hasRssFeed)) {
    throw new Error(
      'Each row must resolve to columns "Show Title" and RSS (or enable "Import without RSS").'
    );
  }

  const customErr = validateMappedRowCustomTypes(mappedData, customHeaders);
  if (customErr) {
    throw new Error(customErr);
  }

  const body = buildScoringRequestBodies(mappedData, {
    isNoRSSFeed,
    customHeaders,
  });

  const { data } = await getScoringImportedShows(
    body as unknown as Record<string, string>[]
  );

  const successUnique: SuggestedSuccessShowProps[] = [];
  data.success.forEach((show) => {
    if (
      successUnique.some(
        (x) => x.showName === show.showName && x.rss === show.rss
      )
    ) {
      return;
    }
    successUnique.push(show);
  });

  const reviewData: ReviewDataProps = {
    ...data,
    success: successUnique,
  };

  const warningsOut: string[] = [];

  if (
    reviewData.success.length > 0 &&
    !reviewData.suggested?.length &&
    !reviewData.invalid?.length &&
    !reviewData.unmatched?.length
  ) {
    return {
      showNames: uniqShowNames(reviewData.success),
      warnings: warningsOut,
    };
  }

  const { reviewed, warnings } = buildAutoReviewedData(reviewData);
  warningsOut.push(...warnings);

  const validationErr = validateReviewedData(reviewed);
  if (validationErr !== null) {
    const fallback = uniqShowNames(reviewData.success);
    warningsOut.unshift(
      `${validationErr} Using ${fallback.length} fully matched shows only.`
    );
    if (fallback.length === 0) {
      throw new Error(validationErr);
    }
    return { showNames: fallback, warnings: warningsOut };
  }

  try {
    const result = await getScoringReviewedShows(reviewed);
    const merged = [...reviewData.success, ...(result.data.success || [])];
    return {
      showNames: uniqShowNames(merged),
      warnings: warningsOut,
    };
  } catch {
    const fallback = uniqShowNames(reviewData.success);
    warningsOut.unshift(
      'Review step failed; returning only matched shows from the first pass.'
    );
    if (!fallback.length) {
      throw new Error(
        'Automatic review failed and no rows were fully matched.'
      );
    }
    return {
      showNames: fallback,
      warnings: warningsOut,
    };
  }
}
